import React, {useState, useEffect} from 'react';
import Dialog from '@material-ui/core/Dialog';
import PropTypes from 'prop-types';
import {FormControl} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import cn from 'classnames';
import MenuItem from '@material-ui/core/MenuItem';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import {DialogBody} from 'routes/App/dashboard/styles';
import DialogTitle from 'components/DialogTitle/styles';
import DialogSubTitle from 'components/DialogSubTitle/styles';
import DialogField from 'components/DialogField/styles';
import DialogFields from 'components/DialogFields/styles';
import DialogActions from 'components/DialogActions/styles';
import DialogAction from 'components/DialogAction/styles';
import FieldLabel from 'components/FieldLabel/styles';
import FieldInput from 'components/FieldInput';
import SelectComponent from 'components/select/SelectComponent';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import {
  AdjustPricingModalContainer,
  PlatformSection,
  PlatformName,
  AmountField,
  TypeField
} from 'routes/App/byocOnboarding/MenuSetup/AdjustPricingModalStyles';
import SelectWrapper from 'components/select/SelectComponentStyles';
import {priceFormatter} from '../../menuEditor/constants';

const PLATFORMS = [
  {id: 'uber', name: 'UberEats'},
  {id: 'grubhub', name: 'GrubHub'},
  {id: 'doordash', name: 'DoorDash'},
  {id: 'slice', name: 'Slice'}
];

const TYPE_OPTIONS = [
  {_id: 'fixed', FullName: '$ Dollar'},
  {_id: 'percentage', FullName: '% Percent'}
];

const AdjustPricingModal = ({
  isOpen,
  onClose,
  onApply,
  initialPricing = [],
  isLoading = false
}) => {
  // Convert AdjustmentPriceRules to pricing format
  const getInitialPricing = (pricingRules) => {
    const defaultPricing = {
      uber: {amount: '', type: 'fixed'},
      grubhub: {amount: '', type: 'fixed'},
      doordash: {amount: '', type: 'fixed'},
      slice: {amount: '', type: 'fixed'}
    };

    if (!pricingRules || pricingRules.length === 0) {
      return defaultPricing;
    }

    const platformMap = {
      uber: 'uber',
      grubhub: 'grubhub',
      doordash: 'doordash',
      slice: 'slice'
    };

    pricingRules.forEach((rule) => {
      // Support both camelCase and PascalCase formats
      const platform = rule.platform || rule.Platform;
      const platformId = platformMap[platform];
      if (platformId) {
        const rateAmountDiff = rule.rateAmountDiff ?? rule.RateAmountDiff;
        const rateType = rule.rateType || rule.RateType || 'fixed';
        defaultPricing[platformId] = {
          amount: rateAmountDiff?.toString() || '',
          type: rateType
        };
      }
    });

    return defaultPricing;
  };

  const [pricing, setPricing] = useState(() =>
    getInitialPricing(initialPricing)
  );

  // Update pricing when modal opens
  useEffect(() => {
    if (isOpen) {
      setPricing(getInitialPricing(initialPricing));
    }
  }, [isOpen]);

  const handleAmountChange = (platformId, value) => {
    setPricing((prev) => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        amount: value
      }
    }));
  };

  const handleTypeChange = (platformId, value) => {
    setPricing((prev) => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        type: value
      }
    }));
  };

  const handleApply = () => {
    onApply(pricing);
  };

  // Prevent closing modal when loading
  const handleClose = (event, reason) => {
    if (isLoading) {
      // Prevent closing when loading
      return;
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} scroll='body' onClose={handleClose}>
      <DialogContainer className='DialogMd'>
        <DialogClose onClick={isLoading ? undefined : onClose} />
        <DialogTitle>Adjust Pricing</DialogTitle>
        <DialogSubTitle className='textMedium textCenter marginT30'>
          Adjust pricing for all connected 3rd party platforms by dollar amount
          or percentage
        </DialogSubTitle>
        <DialogBody>
          <AdjustPricingModalContainer>
            {PLATFORMS.map((platform) => (
              <PlatformSection key={platform.id}>
                <PlatformName>{platform.name}</PlatformName>
                <DialogFields>
                  <DialogField className='w70'>
                    <FieldLabel className='FieldLabelRegular'>
                      Amount
                    </FieldLabel>
                    <AmountField>
                      <FieldInput
                        value={pricing[platform.id].amount}
                        onChange={(e) => {
                          const price = priceFormatter(
                            e.target.value,
                            pricing[platform.id].amount
                          );
                          handleAmountChange(platform.id, price);
                        }}
                        onBlur={(e) => {
                          const lastElement = e.target.value.slice(-1)[0];
                          if (lastElement === '.') {
                            handleAmountChange(
                              platform.id,
                              e.target.value.slice(0, -1)
                            );
                          }
                        }}
                        placeholder='0.00'
                      />
                    </AmountField>
                  </DialogField>
                  <DialogField className='w30'>
                    <FieldLabel className='FieldLabelRegular'>Type</FieldLabel>
                    <SelectWrapper className='SelectMd w100'>
                      <FormControl className='select-formcontrol'>
                        <Select
                          inputProps={{'aria-label': 'value'}}
                          onChange={(e) => {
                            handleTypeChange(platform.id, e.target.value);
                          }}
                          value={pricing[platform.id].type}
                        >
                          {TYPE_OPTIONS.map((item) => (
                            <MenuItem value={item._id}>
                              {item.FullName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SelectWrapper>
                  </DialogField>
                </DialogFields>
              </PlatformSection>
            ))}
          </AdjustPricingModalContainer>
        </DialogBody>
        <DialogActions className='justifyEnd'>
          <DialogAction className='w30'>
            <ButtonOutline onClick={onClose} type='button' disabled={isLoading}>
              Cancel
            </ButtonOutline>
          </DialogAction>
          <DialogAction className='w30'>
            <ButtonPrimary
              onClick={handleApply}
              type='button'
              disabled={isLoading}
            >
              <span>{isLoading ? 'Saving...' : 'Apply'}</span>
            </ButtonPrimary>
          </DialogAction>
        </DialogActions>
      </DialogContainer>
    </Dialog>
  );
};

AdjustPricingModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  initialPricing: PropTypes.array,
  isLoading: PropTypes.bool
};

AdjustPricingModal.defaultProps = {
  initialPricing: [],
  isLoading: false
};

export default AdjustPricingModal;
